import React, { useState, useEffect, useRef } from 'react';
import axios from '../config/axiosConfig';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaWhatsapp,
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaUserFriends, 
  FaGraduationCap, FaHandsHelping, FaHeart, FaBuilding, FaCalendarAlt, FaHandshake,
  FaGlobe, FaChevronLeft, FaChevronRight,
  FaUsers, FaUserCheck, FaBullseye, FaEye, FaCoffee, FaUtensils, 
  FaBreadSlice, FaAppleAlt, FaLeaf, FaSeedling, FaTshirt, FaShoePrints, 
  FaCouch, FaConciergeBell, FaFlask, FaIndustry
} from 'react-icons/fa';
import './AboutPage.css';
import { useTranslation } from '../contexts/TranslationContext';

const AboutPage = () => {
  const { t, language } = useTranslation();

  // ---- Data states ----
  const [aboutData, setAboutData] = useState(null);
  const [coreValues, setCoreValues] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [partners, setPartners] = useState([]);

  // ---- Achievements counter states ----
  const [displayAchievements, setDisplayAchievements] = useState([]);
  const achievementsSectionRef = useRef(null);
  const countersStarted = useRef(false);

  // ---- Contact form states ----
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  // ---- Team carousel states (keep carousel for team) ----
  const [teamCurrentPage, setTeamCurrentPage] = useState(0);
  const [teamAutoPlay, setTeamAutoPlay] = useState(true);
  const teamTimerRef = useRef(null);

  // ---- Refs for scroll animation sections ----
  const missionVisionRef = useRef(null);
  const valuesRef = useRef(null);
  const departmentsRef = useRef(null);
  const teamRef = useRef(null);
  const contactSocialRef = useRef(null);
  const partnersRef = useRef(null);

  // ---- Helper functions ----
  const getArrayData = (responseData) => {
    if (!responseData) return [];
    if (Array.isArray(responseData)) return responseData;
    if (responseData.data && Array.isArray(responseData.data)) return responseData.data;
    if (responseData.rows && Array.isArray(responseData.rows)) return responseData.rows;
    if (responseData.result && Array.isArray(responseData.result)) return responseData.result;
    if (responseData.items && Array.isArray(responseData.items)) return responseData.items;
    if (responseData.values && Array.isArray(responseData.values)) return responseData.values;
    if (responseData.achievements && Array.isArray(responseData.achievements)) return responseData.achievements;
    if (responseData.team && Array.isArray(responseData.team)) return responseData.team;
    if (responseData.social && Array.isArray(responseData.social)) return responseData.social;
    if (responseData.departments && Array.isArray(responseData.departments)) return responseData.departments;
    if (responseData.partners && Array.isArray(responseData.partners)) return responseData.partners;
    if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) return [responseData];
    console.warn('Unexpected API response format:', responseData);
    return [];
  };

  const getObjectData = (responseData) => {
    if (!responseData) return null;
    if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
      if (responseData.data && typeof responseData.data === 'object') return responseData.data;
      if (responseData.about && typeof responseData.about === 'object') return responseData.about;
      if (responseData.contact && typeof responseData.contact === 'object') return responseData.contact;
      if (responseData.info && typeof responseData.info === 'object') return responseData.info;
      return responseData;
    }
    if (Array.isArray(responseData) && responseData.length > 0) return responseData[0];
    return null;
  };

  const parseAchievementValue = (value) => {
    if (!value) return 0;
    const numStr = String(value).replace(/[^0-9.]/g, '');
    const num = parseFloat(numStr);
    return isNaN(num) ? 0 : num;
  };

  const startAchievementCounters = (targets) => {
    if (countersStarted.current) return;
    countersStarted.current = true;

    const duration = 2000;
    const steps = 60;
    const intervals = targets.map((target, index) => {
      const increment = target.target / steps;
      let current = 0;
      let step = 0;

      return setInterval(() => {
        step++;
        current = Math.min(current + increment, target.target);
        const rounded = Math.floor(current);
        setDisplayAchievements(prev => {
          const newArr = [...prev];
          if (newArr[index]) {
            newArr[index] = { ...newArr[index], current: rounded };
          }
          return newArr;
        });
        if (step >= steps) clearInterval(intervals[index]);
      }, duration / steps);
    });

    return () => intervals.forEach(clearInterval);
  };

  // ============================================================
  // FALLBACK ACHIEVEMENTS
  // ============================================================
  const fallbackAchievements = [
    { id: 1, achievement_number: '705+', achievement_label: 'Women Trained In TVET Program 2025',     icon_class: 'FaUsers' },
    { id: 2, achievement_number: '350',  achievement_label: 'Ongoing Trained Women 2026',             icon_class: 'FaHandsHelping' },
    { id: 3, achievement_number: '443',  achievement_label: 'Women Recruited By Company',             icon_class: 'FaUserCheck' },
    { id: 4, achievement_number: '114',  achievement_label: 'Women Trained In Computer And Literacy', icon_class: 'FaGraduationCap' },
    { id: 5, achievement_number: '10',   achievement_label: 'Departments',                            icon_class: 'FaUsers' }
  ];

  // ============================================================
  // FALLBACK DEPARTMENTS (10 new departments)
  // ============================================================
  const fallbackDepartments = () => [
    { id: 1,  name: t('deptCoffeeShop') || 'Coffee Shop Operations',                    description: t('deptCoffeeShopDesc') || 'Professional barista training, coffee preparation, and cafe management' },
    { id: 2,  name: t('deptHousekeeping') || 'Housekeeping Operations',                 description: t('deptHousekeepingDesc') || 'Professional cleaning, organization, and hospitality management' },
    { id: 3,  name: t('deptCarpetYarn') || 'Carpet & Yarn Production',                  description: t('deptCarpetYarnDesc') || 'Hand-woven carpet making and yarn production using traditional and modern methods' },
    { id: 4,  name: t('deptLeatherFootwear') || 'Leather Goods and Footwear Design and Production', description: t('deptLeatherFootwearDesc') || 'Leather goods production including bags, shoes, and accessories' },
    { id: 5,  name: t('deptSewingGarment') || 'Sewing and Garment Production',          description: t('deptSewingGarmentDesc') || 'Professional sewing, tailoring, and garment production' },
    { id: 6,  name: t('deptMilkProcessing') || 'Milk Processing',                       description: t('deptMilkProcessingDesc') || 'Milk and yogurt processing, packaging, and dairy product development' },
    { id: 7,  name: t('deptBakeryPastry') || 'Bakery & Pastry Production',              description: t('deptBakeryPastryDesc') || 'Professional baking, pastry making, and confectionery skills' },
    { id: 8,  name: t('deptNutritionalFood') || 'Nutritional Food Production',          description: t('deptNutritionalFoodDesc') || 'Production of nutritious food products and balanced meal preparation' },
    { id: 9,  name: t('deptReception') || 'Reception Operations',                       description: t('deptReceptionDesc') || 'Professional receptionist, customer service, and office management' },
    { id: 10, name: t('deptHorticulture') || 'Horticulture Production (Agriculture)',   description: t('deptHorticultureDesc') || 'Sustainable horticulture production, vegetable farming, and agricultural practices' }
  ];

  // ---- Fetch data ----
  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = { lang: language === 'en' ? undefined : language };

        const [
          aboutRes,
          valuesRes,
          achievementsRes,
          teamRes,
          socialRes,
          contactRes,
          departmentsRes,
          partnersRes
        ] = await Promise.all([
          axios.get('https://hsn.urugowoc.org.rw/backend/api/about', { params }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/core-values', { params }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/achievements', { params }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/team-members', { params: { ...params, active: true, sort: 'sort_order' } }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/social-media', { params: { ...params, active: true } }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/contact-info', { params }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/departments', { params: { ...params, active: true } }),
          axios.get('https://hsn.urugowoc.org.rw/backend/api/partners', { params: { ...params, active: true } })
        ]);

        setAboutData(getObjectData(aboutRes.data));
        setCoreValues(getArrayData(valuesRes.data));

        const rawAchievements = getArrayData(achievementsRes.data);
        const finalAchievements = rawAchievements.length > 0 ? rawAchievements : fallbackAchievements;
        const displayData = finalAchievements.map(item => ({
          ...item,
          current: 0,
          target: parseAchievementValue(item.achievement_number || item.number || item.value)
        }));
        setAchievements(finalAchievements);
        setDisplayAchievements(displayData);

        setTeamMembers(getArrayData(teamRes.data));
        setSocialMedia(getArrayData(socialRes.data));
        setContactInfo(getObjectData(contactRes.data));
        setDepartments(getArrayData(departmentsRes.data));
        setPartners(getArrayData(partnersRes.data));

        countersStarted.current = false;

      } catch (error) {
        console.error('Error fetching data:', error);
        setAboutData({
          title: t('aboutUs') || 'About Urugo Women Opportunity Center',
          mission: t('defaultMission') || 'To empower women through vocational training and create sustainable livelihoods.',
          vision: t('defaultVision') || 'A world where every woman has the skills and opportunities to thrive.',
          history: t('defaultHistory') || 'Founded in 2019, Urugo Women Opportunity Center has been transforming lives through skills training.',
          description: t('aboutDescription') || 'We are dedicated to empowering women through comprehensive vocational training programs.'
        });

        setCoreValues([
          { id: 1, value_text: t('empowerment') || 'Empowerment', description: t('empowermentDesc') || 'Empowering women to reach their full potential' },
          { id: 2, value_text: t('integrity') || 'Integrity', description: t('integrityDesc') || 'Maintaining honesty and ethical standards in all we do' },
          { id: 3, value_text: t('community') || 'Community', description: t('communityDesc') || 'Building strong, supportive communities' },
          { id: 4, value_text: t('excellence') || 'Excellence', description: t('excellenceDesc') || 'Striving for excellence in all our programs' }
        ]);

        setAchievements(fallbackAchievements);
        setDisplayAchievements(fallbackAchievements.map(a => ({ ...a, current: 0, target: parseAchievementValue(a.achievement_number) })));

        setTeamMembers([
          { id: 1, full_name: t('directorName') || 'Jane Doe', position: t('director') || 'Center Director', department: 'Management', bio: t('directorBio') || 'Leading the center with passion and dedication.', image_url: '/team1.jpg' },
          { id: 2, full_name: t('trainerName') || 'Mary Smith', position: t('leadTrainer') || 'Lead Trainer', department: t('tailoring') || 'Tailoring', bio: t('trainerBio') || 'Expert in fashion design and sewing techniques.', image_url: '/team2.jpg' }
        ]);
        setSocialMedia([
          { id: 1, platform_name: 'Facebook', platform_url: 'https://web.facebook.com/search/top/?q=urugo%20women%27s%20opportunity%20center', handle: '@urugowoc', icon_class: 'FaFacebook', color_code: '#1877F2' },
          { id: 2, platform_name: 'Twitter', platform_url: 'https://x.com/UrugoWoc', handle: '@urugowoc', icon_class: 'FaTwitter', color_code: '#1DA1F2' },
          { id: 3, platform_name: 'Instagram', platform_url: 'https://www.instagram.com/urugo_woc', handle: '@urugowoc', icon_class: 'FaInstagram', color_code: '#E4405F' },
          { id: 4, platform_name: 'YouTube', platform_url: 'https://www.youtube.com/@urugowoc3033', handle: '@urugowoc', icon_class: 'FaYoutube', color_code: '#FF0000' }
        ]);
        setDepartments(fallbackDepartments());
        setContactInfo({
          email: 'info@urugowoc.com',
          phone: '+250 788 350 577',
          phone2: '+250 788 350 577',
          address: 'Kayonza District, Rwanda\nP.O. Box 1100',
          working_hours: t('workingHoursDetail') || 'Monday - Friday: 8:00 AM - 5:00 PM\nSaturday: 9:00 AM - 1:00 PM\nSunday: Closed'
        });
        setPartners([
          { id: 1, partner_name: t('partner1Name') || 'Ministry of Gender', partner_type: 'Government', description: t('partner1Desc') || 'Government partnership for women empowerment' },
          { id: 2, partner_name: t('partner2Name') || 'UN Women', partner_type: 'International NGO', description: t('partner2Desc') || 'International organization supporting gender equality' },
          { id: 3, partner_name: t('partner3Name') || 'Bank of Kigali', partner_type: 'Corporate', description: t('partner3Desc') || 'Financial partner providing microloans' },
          { id: 4, partner_name: t('partner4Name') || 'Rwanda Development Board', partner_type: 'Government', description: t('partner4Desc') || 'Strategic partner for economic development' },
          { id: 5, partner_name: t('partner5Name') || 'USAID Rwanda', partner_type: 'International NGO', description: t('partner5Desc') || 'Development partner funding vocational training' }
        ]);

        countersStarted.current = false;
      }
    };

    fetchData();
  }, [language, t]);

  // ---- Achievement counter observer ----
  useEffect(() => {
    if (!achievementsSectionRef.current || displayAchievements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !countersStarted.current) {
            const targets = displayAchievements.map(a => ({
              target: a.target || parseAchievementValue(a.achievement_number || a.number || a.value)
            }));
            if (targets.some(t => t.target > 0)) startAchievementCounters(targets);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(achievementsSectionRef.current);
    return () => {
      if (achievementsSectionRef.current) observer.unobserve(achievementsSectionRef.current);
    };
  }, [displayAchievements]);

  // ---- Team carousel helpers ----
  const teamPagesCount = Math.ceil(teamMembers.length / 4);

  const nextTeamPage = () => { setTeamCurrentPage((p) => (p + 1) % teamPagesCount); resetTeamTimer(); };
  const prevTeamPage = () => { setTeamCurrentPage((p) => (p - 1 + teamPagesCount) % teamPagesCount); resetTeamTimer(); };

  const resetTeamTimer = () => {
    if (teamTimerRef.current) clearInterval(teamTimerRef.current);
    if (teamAutoPlay && teamPagesCount > 1) {
      teamTimerRef.current = setInterval(() => setTeamCurrentPage((p) => (p + 1) % teamPagesCount), 4000);
    }
  };

  const stopTeamAutoPlay = () => { setTeamAutoPlay(false); if (teamTimerRef.current) clearInterval(teamTimerRef.current); };
  const startTeamAutoPlay = () => { setTeamAutoPlay(true); resetTeamTimer(); };

  useEffect(() => {
    if (teamPagesCount > 1) resetTeamTimer();
    return () => {
      if (teamTimerRef.current) clearInterval(teamTimerRef.current);
    };
  }, [teamPagesCount]);

  // ---- Scroll animations observer ----
  useEffect(() => {
    const sections = [
      missionVisionRef.current,
      valuesRef.current,
      departmentsRef.current,
      teamRef.current,
      contactSocialRef.current,
      partnersRef.current
    ].filter(ref => ref !== null);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [aboutData, coreValues, achievements, teamMembers, departments, partners]);

  // ---- Contact form handlers ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const response = await axios.post('https://hsn.urugowoc.org.rw/backend/api/contact', formData);

      if (response.data.success || response.data.message) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('Invalid response from server');
      }

      setTimeout(() => setFormStatus(''), 5000);
    } catch (error) {
      console.error('Error sending message:', error);
      setFormStatus('error');
      setTimeout(() => {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 1000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ---- Icon mapping ----
  const iconMap = {
    FaUserFriends: FaUserFriends,
    FaGraduationCap: FaGraduationCap,
    FaHandsHelping: FaHandsHelping,
    FaHeart: FaHeart,
    FaBuilding: FaBuilding,
    FaCalendarAlt: FaCalendarAlt,
    FaHandshake: FaHandshake,
    FaFacebook: FaFacebook,
    FaTwitter: FaTwitter,
    FaInstagram: FaInstagram,
    FaLinkedin: FaLinkedin,
    FaYoutube: FaYoutube,
    FaWhatsapp: FaWhatsapp,
    FaGlobe: FaGlobe,
    FaUsers: FaUsers,
    FaUserCheck: FaUserCheck,
    FaCoffee: FaCoffee,
    FaUtensils: FaUtensils,
    FaBreadSlice: FaBreadSlice,
    FaAppleAlt: FaAppleAlt,
    FaLeaf: FaLeaf,
    FaSeedling: FaSeedling,
    FaTshirt: FaTshirt,
    FaShoePrints: FaShoePrints,
    FaCouch: FaCouch,
    FaConciergeBell: FaConciergeBell,
    FaFlask: FaFlask,
    FaIndustry: FaIndustry
  };

  const getIconComponent = (iconClass) => iconMap[iconClass] || FaGlobe;

  // Return a matching icon for a department based on its name
  const getDepartmentIcon = (deptName) => {
    const name = (deptName || '').toLowerCase();
    if (name.includes('coffee')) return <FaCoffee />;
    if (name.includes('housekeep')) return <FaCouch />;
    if (name.includes('carpet') || name.includes('yarn')) return <FaIndustry />;
    if (name.includes('leather') || name.includes('footwear') || name.includes('shoe')) return <FaShoePrints />;
    if (name.includes('sewing') || name.includes('garment') || name.includes('tailor')) return <FaTshirt />;
    if (name.includes('milk')) return <FaFlask />;
    if (name.includes('bakery') || name.includes('pastry') || name.includes('bread')) return <FaBreadSlice />;
    if (name.includes('nutritional') || name.includes('food')) return <FaAppleAlt />;
    if (name.includes('reception')) return <FaConciergeBell />;
    if (name.includes('horticulture') || name.includes('agriculture') || name.includes('farm')) return <FaSeedling />;
    return <FaGraduationCap />;
  };

  // ✅ Marquee duration based on number of partners
  const partnerMarqueeDuration = Math.max(30, partners.length * 6);

  return (
    <div className="about-page">
      {/* =========================
          HERO
         ========================= */}
      <section className="about-page-hero">
        <div className="about-page-hero-overlay" />
        <div className="about-page-container about-page-hero-content">
          <span className="about-page-hero-tag">
            <FaHeart /> {t('ourStory') || 'Our Story'}
          </span>
          <h1>{aboutData?.title || aboutData?.name || t('aboutUs')}</h1>
          <p className="about-page-hero-subtitle">
            {aboutData?.description || t('empoweringWomenTransformingCommunities') || "Empowering women, transforming communities since 2019"}
          </p>
        </div>
      </section>

      {/* =========================
          MISSION, VISION & HISTORY
         ========================= */}
      <section ref={missionVisionRef} className="about-page-mission-vision-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-three-col-grid">
            <div className="about-page-pillar-card about-page-mission-card">
              <div className="about-page-card-icon-wrap about-page-mission-icon">
                <FaBullseye />
              </div>
              <h2>{t('ourMission') || 'Our Mission'}</h2>
              <p>{aboutData?.mission || aboutData?.mission_statement || t('defaultMission')}</p>
            </div>
            <div className="about-page-pillar-card about-page-vision-card">
              <div className="about-page-card-icon-wrap about-page-vision-icon">
                <FaEye />
              </div>
              <h2>{t('ourVision') || 'Our Vision'}</h2>
              <p>{aboutData?.vision || aboutData?.vision_statement || t('defaultVision')}</p>
            </div>
            <div className="about-page-pillar-card about-page-history-card">
              <div className="about-page-card-icon-wrap about-page-history-icon">
                <FaClock />
              </div>
              <h2>{t('Our History') || 'Our History'}</h2>
              <p>{aboutData?.history || aboutData?.story || t('defaultHistory')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================
          ACHIEVEMENTS
         ========================= */}
      {/* <section ref={achievementsSectionRef} className="about-page-achievements-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-section-header">
            <span className="about-page-section-tag">{t('impact') || 'Impact'}</span>
            <h2 className="about-page-section-title">{t('Our Achievements') || 'Our Achievements'}</h2>
          </div>
          <div className="about-page-achievements-row">
            {displayAchievements.map((achievement, index) => {
              const IconComponent = getIconComponent(achievement.icon_class);
              const suffix = (achievement.achievement_number || achievement.number || achievement.value || '').replace(/[0-9.]/g, '');
              const displayValue = achievement.current + suffix;
              return (
                <div key={achievement.id || index} className="about-page-achievement-card">
                  <div className="about-page-achievement-icon"><IconComponent /></div>
                  <h3>{displayValue}</h3>
                  <p>{achievement.achievement_label || achievement.label || achievement.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* =========================
          CORE VALUES
         ========================= */}
      <section ref={valuesRef} className="about-page-values-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-section-header">
            <span className="about-page-section-tag">{t('whatDrivesUs') || 'What Drives Us'}</span>
            <h2 className="about-page-section-title">{t('ourCoreValues') || 'Our Core Values'}</h2>
          </div>
          <div className="about-page-values-grid-4">
            {coreValues.map((value, index) => (
              <div key={value.id || index} className="about-page-value-card">
                <div className="about-page-value-number">{String(index + 1).padStart(2, '0')}</div>
                <h3>{value.value_text || value.name || value.title}</h3>
                <p>{value.description || value.value_description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================
          DEPARTMENTS — 10 departments, 5 per row, no borders
         ========================= */}
      <section ref={departmentsRef} className="about-page-departments-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-section-header">
            <span className="about-page-section-tag">{t('whatWeTeach') || 'What We Teach'}</span>
            <h2 className="about-page-section-title">{t('ourTrainingDepartments') || 'Our Training Departments'}</h2>
          </div>
          {departments.length > 0 ? (
            <div className="about-page-departments-grid-5">
              {departments.map((dept, idx) => (
                <div key={dept.id || idx} className="about-page-department-card">
                  <div className="about-page-department-icon-wrap">
                    {getDepartmentIcon(dept.name || dept.department_name)}
                  </div>
                  <h3>{dept.name || dept.department_name}</h3>
                  <p>{dept.description || dept.department_description}</p>
                  {dept.duration && (
                    <span className="about-page-department-duration">
                      <FaClock /> {t('duration') || 'Duration'}: {dept.duration}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="about-page-no-data-message"><p>{t('noDepartmentsAvailable') || 'No departments information available.'}</p></div>
          )}
        </div>
      </section>

      {/* =========================
          TEAM CAROUSEL
         ========================= */}
      <section ref={teamRef} className="about-page-team-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-section-header">
            <span className="about-page-section-tag">{t('thePeople') || 'The People'}</span>
            <h2 className="about-page-section-title">{t('meetOurTeam') || 'Meet Our Team'}</h2>
          </div>
          {teamMembers.length > 0 ? (
            <div
              className="about-page-team-carousel-container"
              onMouseEnter={stopTeamAutoPlay}
              onMouseLeave={startTeamAutoPlay}
            >
              {teamPagesCount > 1 && (
                <button className="about-page-carousel-nav about-page-carousel-prev" onClick={prevTeamPage} aria-label="Previous">
                  <FaChevronLeft />
                </button>
              )}
              <div className="about-page-team-carousel-wrapper">
                <div
                  className="about-page-team-carousel-track"
                  style={{ transform: `translateX(-${teamCurrentPage * 100}%)` }}
                >
                  {Array.from({ length: teamPagesCount }).map((_, pageIdx) => (
                    <div key={pageIdx} className="about-page-team-slide">
                      <div className="about-page-team-grid-4">
                        {teamMembers.slice(pageIdx * 4, (pageIdx + 1) * 4).map(member => (
                          <div key={member.id} className="about-page-team-card">
                            <div className="about-page-team-info">
                              <div className="about-page-team-avatar">
                                {member.image_url ? (
                                  <img src={member.image_url} alt={member.full_name || member.name}
                                       onError={(e) => { e.target.style.display='none'; }} />
                                ) : (
                                  <span>{(member.full_name || member.name || '?').charAt(0).toUpperCase()}</span>
                                )}
                              </div>
                              <h3>{member.full_name || member.name}</h3>
                              <p className="about-page-position">{member.position || member.role || member.title}</p>
                              {member.department && <p className="about-page-department">{member.department || member.team}</p>}
                              <p className="about-page-bio">{member.bio || member.description || member.about}</p>
                              <div className="about-page-team-contact">
                                {member.email && <a href={`mailto:${member.email}`}>{member.email}</a>}
                                {member.phone && <p>{member.phone}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {teamPagesCount > 1 && (
                <button className="about-page-carousel-nav about-page-carousel-next" onClick={nextTeamPage} aria-label="Next">
                  <FaChevronRight />
                </button>
              )}
              {teamPagesCount > 1 && (
                <div className="about-page-carousel-dots">
                  {Array.from({ length: teamPagesCount }).map((_, idx) => (
                    <button key={idx} className={`about-page-carousel-dot ${idx === teamCurrentPage ? 'active' : ''}`} onClick={() => { setTeamCurrentPage(idx); resetTeamTimer(); }} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="about-page-no-data-message"><p>{t('noTeamInfoAvailable') || 'No team information available.'}</p></div>
          )}
        </div>
      </section>

      {/* =========================
          CONTACT & SOCIAL + FORM
         ========================= */}
      <section ref={contactSocialRef} className="about-page-contact-social-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-grid-2">

            {/* LEFT — contact info + social icons */}
            <div className="about-page-contact-info">
              <span className="about-page-section-tag">{t('Get In Touch') || 'Get In Touch'}</span>
              <h2 className="about-page-section-title about-page-section-title-left">
                {t('contactUs') || 'Contact Us'}
              </h2>

              <div className="about-page-contact-details">
                {contactInfo?.email && (
                  <div className="about-page-contact-item">
                    <div className="about-page-contact-icon-wrap about-page-contact-icon-email">
                      <FaEnvelope />
                    </div>
                    <div>
                      <h3>{t('email') || 'Email'}</h3>
                      <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                    </div>
                  </div>
                )}

                {contactInfo?.phone && (
                  <div className="about-page-contact-item">
                    <div className="about-page-contact-icon-wrap about-page-contact-icon-phone">
                      <FaPhone />
                    </div>
                    <div>
                      <h3>{t('phone') || 'Phone'}</h3>
                      <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
                      {contactInfo?.phone2 && (
                        <p className="about-page-small-text">
                          {t('alternate') || 'Alternate'}: {contactInfo.phone2}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {contactInfo?.address && (
                  <div className="about-page-contact-item">
                    <div className="about-page-contact-icon-wrap about-page-contact-icon-address">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <h3>{t('address') || 'Address'}</h3>
                      <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{contactInfo.address}</p>
                    </div>
                  </div>
                )}

                {contactInfo?.working_hours && (
                  <div className="about-page-contact-item">
                    <div className="about-page-contact-icon-wrap about-page-contact-icon-hours">
                      <FaClock />
                    </div>
                    <div>
                      <h3>{t('workingHours') || 'Working Hours'}</h3>
                      <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{contactInfo.working_hours}</p>
                    </div>
                  </div>
                )}
              </div>

              {socialMedia.length > 0 && (
                <div className="about-page-social-icons-wrap">
                  <h3 className="about-page-social-icons-title">
                    {t('connectWithUs') || 'Connect With Us'}
                  </h3>
                  <div className="about-page-social-icons-row">
                    {socialMedia.map(social => {
                      const IconComponent = getIconComponent(social.icon_class);
                      return (
                        <a
                          key={social.id}
                          href={social.platform_url || social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="about-page-social-icon-btn"
                          style={{ backgroundColor: social.color_code || '#3498db' }}
                          title={social.platform_name || social.name}
                          aria-label={social.platform_name || social.name}
                        >
                          <IconComponent />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT — contact form */}
            <div className="about-page-contact-form-inline">
              <div className="about-page-form-header">
                <span className="about-page-section-tag">{t('Drop Us a Line') || 'Drop Us a Line'}</span>
                <h2 className="about-page-section-title about-page-section-title-left">
                  {t('sendUsAMessage') || 'Send Us a Message'}
                </h2>
                <p className="about-page-section-subtitle about-page-section-subtitle-left">
                  {t('wellGetBackToYou') || "We'll get back to you as soon as possible"}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="about-page-contact-form">
                <div className="about-page-form-grid">
                  <div className="about-page-form-group">
                    <label htmlFor="name">{t('fullName') || 'Full Name *'}</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder={t('formNamePlaceholder') || "Enter your full name"} />
                  </div>
                  <div className="about-page-form-group">
                    <label htmlFor="email">{t('emailAddress') || 'Email Address *'}</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder={t('formEmailPlaceholder') || "Enter your email address"} />
                  </div>
                </div>

                <div className="about-page-form-group">
                  <label htmlFor="subject">{t('subject') || 'Subject *'}</label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required placeholder={t('formSubjectPlaceholder') || "Enter subject"} />
                </div>

                <div className="about-page-form-group">
                  <label htmlFor="message">{t('message') || 'Message *'}</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required rows="6" placeholder={t('formMessagePlaceholder') || "Type your message here..."}></textarea>
                </div>

                <button type="submit" className="about-page-submit-btn" disabled={formStatus === 'sending'}>
                  {formStatus === 'sending' ? (t('sending') || 'Sending...') : (t('sendMessage') || 'Send Message')}
                </button>

                {formStatus === 'success' && (
                  <div className="about-page-form-success">
                    {t('thankYouMessageSent') || 'Thank you! Your message has been sent successfully.'}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="about-page-form-error">
                    {t('sorryErrorSending') || 'Sorry, there was an error sending your message. Please try again.'}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* =========================
          PARTNERS — MARQUEE
         ========================= */}
      <section ref={partnersRef} className="about-page-partners-section about-page-fade-up">
        <div className="about-page-container">
          <div className="about-page-section-header">
            <span className="about-page-section-tag">{t('togetherWeWin') || 'Together We Win'}</span>
            <h2 className="about-page-section-title">{t('ourPartnersSponsors') || 'Our Partners & Sponsors'}</h2>
            <p className="about-page-section-subtitle">{t('partnersDescription') || 'Working together to empower women and transform communities'}</p>
          </div>
          {partners.length > 0 ? (
            <div
              className="about-page-partners-marquee"
              style={{ '--partner-duration': `${partnerMarqueeDuration}s` }}
            >
              <div className="about-page-partners-marquee-track">
                {/* First copy */}
                {partners.map((partner, idx) => (
                  <div key={`a-${partner.id || idx}`} className="about-page-partner-card">
                    <div className="about-page-partner-icon-wrap">
                      <FaHandshake />
                    </div>
                    <h4>{partner.partner_name}</h4>
                    {partner.partner_type && (
                      <span className="about-page-partner-type">{partner.partner_type}</span>
                    )}
                    <p>{partner.description || t('partnerDescriptionDefault') || 'Partner organization'}</p>
                    {partner.website_url && (
                      <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="about-page-partner-link">
                        <FaGlobe /> {t('visitWebsite') || 'Visit Website'}
                      </a>
                    )}
                  </div>
                ))}
                {/* Second copy for seamless loop */}
                {partners.map((partner, idx) => (
                  <div key={`b-${partner.id || idx}`} className="about-page-partner-card" aria-hidden="true">
                    <div className="about-page-partner-icon-wrap">
                      <FaHandshake />
                    </div>
                    <h4>{partner.partner_name}</h4>
                    {partner.partner_type && (
                      <span className="about-page-partner-type">{partner.partner_type}</span>
                    )}
                    <p>{partner.description || t('partnerDescriptionDefault') || 'Partner organization'}</p>
                    {partner.website_url && (
                      <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="about-page-partner-link">
                        <FaGlobe /> {t('visitWebsite') || 'Visit Website'}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="about-page-no-data-message"><p>{t('noPartnersAvailable') || 'No partners information available.'}</p></div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AboutPage;